<template>
  <div class="two-factor-disable">
    <h6 class="sub-section-title">
      Disable Two-Factor Authentication
    </h6>

    <div class="info-content">
      <div class="info-comment">
        You are about to disable two-factor authentication for your account.
        <br>
        This action requires your current authentication code and cannot be undone.
      </div>

      <div
        v-if="!status.isSuccess"
        class="disable-section mt-4"
      >
        <div class="verification-code">
          <input 
            v-model="disableToken" 
            type="text" 
            class="form-control"
            placeholder="Enter OTP Code"
            :disabled="status.isVerifying"
            pattern="[0-9]*"
            maxlength="6"
            @keyup.enter="handleDisable"
          >
          <div class="button-group mt-3">
            <button 
              class="btn btn-danger" 
              :disabled="status.isVerifying || !disableToken"
              @click="handleDisable"
            >
              {{ status.isVerifying ? 'Disabling...' : 'Disable 2FA' }}
            </button>
            <button 
              class="btn btn-secondary ms-2" 
              :disabled="status.isVerifying"
              @click="handleCancel"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>

      <message-html :result-html="status.resultHtml" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { reactive, ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useUserType } from '@/use/userType';

import { sendGet, sendPost } from '@/Utils/api';
import util from '@/Utils/util';

const router = useRouter();
const { userType } = useUserType();
const disableToken = ref('');

const status = reactive({
  isLoading: false,
  isVerifying: false,
  resultHtml: '',
  isSuccess: false
});

const checkStatus = async () => {
  status.isLoading = true;
  try {
    const response = await sendGet('2fa/status');
    if (!response.data.enabled) {
      status.resultHtml = util.getErrorResponse({ response: { data: { message: 'Two-factor authentication is not enabled.' } } });
      setTimeout(() => {
        router.push(`/${userType.value}/2fa`);
      }, 2000);
    }
  } catch (error: any) {
    if (error.response?.status === 401) {
      router.push({
        path: `/${userType.value}/re-authenticate`,
        query: { returnUrl: `/${userType.value}/2fa/disable` }
      });
      return;
    }
    status.resultHtml = util.getErrorResponse(error);
  } finally {
    status.isLoading = false;
  }
};

const handleDisable = async () => {
  if (!disableToken.value || status.isVerifying) return;

  status.isVerifying = true;
  try {
    await sendPost('2fa/disable', { token: disableToken.value });
    status.resultHtml = util.setSuccessMessage('Two-factor authentication has been disabled successfully.');
    status.isSuccess = true;
    setTimeout(() => {
      router.push(`/${userType.value}/2fa`);
    }, 2000);
  } catch (error: any) {
    if (error.response?.status === 401) {
      router.push({
        path: `/${userType.value}/re-authenticate`,
        query: { returnUrl: `/${userType.value}/2fa/disable` }
      });
      return;
    }
    status.resultHtml = util.getErrorResponse(error);
  } finally {
    status.isVerifying = false;
  }
};

const handleCancel = () => {
  if (status.isVerifying) return;
  router.push(`/${userType.value}/2fa`);
};

onMounted(checkStatus)
</script>

<style lang="scss">
.two-factor-disable {
  .info-content {
    padding: 20px;
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);

    .info-comment {
      color: #666;
      font-size: 14px;
      line-height: 1.5;
    }

    .disable-section {
      text-align: center;
      
      .verification-code {
        max-width: 300px;
        margin: 0 auto;
      }

      .button-group {
        display: flex;
        justify-content: center;
        gap: 10px;
      }
    }
  }
}
</style> 